import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  X, 
  Calendar, 
  Clock, 
  Phone, 
  Mail, 
  Link, 
  User, 
  Users, 
  MessageSquare,
  Video,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, parseISO } from 'date-fns';
import useEscKeyHandler from '../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { tenantAdminAPI } from '../services/api/tenantAdminAPI';

const SchedulingModal = ({ isOpen, onClose, onSubmit, existingSchedules = [] }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [communicationMethod, setCommunicationMethod] = useState('link');
  const [selectedPatient, setSelectedPatient] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    defaultValues: {
      title: '',
      description: '',
      patient_id: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      time: '09:00',
      communication_method: 'link',
      notes: ''
    }
  });

  // Add Esc key handler for modal
  useEscKeyHandler(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      loadPatients();
      setSelectedDate(new Date());
      setSelectedTimes([]);
      setCommunicationMethod('link');
      reset();
    }
  }, [isOpen]);

  const loadPatients = async () => {
    try {
      const response = await tenantAdminAPI.getPatients();
      setPatients(response.data || []);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    }
  };

  // Generate calendar days
  const calendarDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const timeSlots = [
    '06:00','07:00','08:00','09:00','10:00','11:00',
    '12:00','13:00','14:00','15:00',
    '16:00','17:00','18:00','19:00', '20:00','21:00','22:00',
  ];

  const communicationMethods = [
    { id: 'link', label: 'Send Link / SMS', icon: Link, color: 'text-purple-600' },
    { id: 'call', label: 'Phone Call', icon: Phone, color: 'text-blue-600' },
  ];

  // Function to check if a time slot is disabled (already passed)
  const isTimeDisabled = (time) => {
    const today = new Date();
    const selectedDateOnly = new Date(selectedDate);
    selectedDateOnly.setHours(0, 0, 0, 0);
    const todayOnly = new Date(today);
    todayOnly.setHours(0, 0, 0, 0);
    
    // If the selected date is in the past, all times are disabled
    if (selectedDateOnly < todayOnly) {
      return true;
    }
    
    // If selecting today's date, check if the time has passed
    if (selectedDateOnly.getTime() === todayOnly.getTime()) {
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
      const [hours, minutes] = time.split(':').map(Number);
      const scheduledTime = hours * 60 + minutes;
      
      return scheduledTime <= currentTime;
    }
    
    return false; // Future dates are not disabled
  };

  const handleFormSubmit = async (data) => {
    try {
      setLoading(true);
      
      if (!selectedPatient) {
        toast.error('Please select a patient');
        return;
      }
      
      console.log('SchedulingModal: Selected patient ID:', selectedPatient);
      console.log('SchedulingModal: Selected date:', format(selectedDate, 'yyyy-MM-dd'));
      console.log('SchedulingModal: Selected times:', selectedTimes);
      console.log('SchedulingModal: Current date/time:', new Date().toISOString());
      console.log('SchedulingModal: Selected date object:', selectedDate);

      if (selectedTimes.length === 0) {
        toast.error('Please select at least one time slot');
        return;
      }
      
      // Check if the selected date is in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      const selectedDateOnly = new Date(selectedDate);
      selectedDateOnly.setHours(0, 0, 0, 0);
      
      if (selectedDateOnly < today) {
        toast.error('Please select a future date for scheduling');
        return;
      }
      
      // If selecting today's date, check if the time is in the future
      if (selectedDateOnly.getTime() === today.getTime()) {
        const now = new Date();
        const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes
        
        for (const time of selectedTimes) {
          const [hours, minutes] = time.split(':').map(Number);
          const scheduledTime = hours * 60 + minutes;
          
          if (scheduledTime <= currentTime) {
            toast.error(`Time ${time} has already passed today. Please select a future time.`);
            return;
          }
        }
      }

      // For each selected time, schedule an email
      const scheduledJobs = [];
      
      for (const time of selectedTimes) {
        // Ensure time format is HH:MM (remove seconds if present)
        const formattedTime = time.includes(':') ? time.substring(0, 5) : time;
        
        const scheduleData = {
          patient_id: selectedPatient,
          scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
          scheduled_time: formattedTime,
          title: data.title,
          description: data.description,
          notes: data.notes
        };
        
        console.log(`SchedulingModal: Creating schedule data for time ${time}:`, scheduleData);

        try {
          const response = await tenantAdminAPI.scheduleRecordingEmail(scheduleData);
          scheduledJobs.push(response.data);
        } catch (error) {
          console.error(`Failed to schedule email for time ${time}:`, error);
          console.error(`Error response:`, error.response?.data);
          console.error(`Error status:`, error.response?.status);
          console.error(`Error detail:`, error.response?.data?.detail);
          console.error(`Schedule data:`, scheduleData);
          toast.error(`Failed to schedule email for ${time}: ${error.response?.data?.detail || error.message}`);
        }
      }

      if (scheduledJobs.length > 0) {
        toast.success(`Successfully scheduled ${scheduledJobs.length} recording email(s)!`);
        onSubmit({
          ...data,
          date: format(selectedDate, 'yyyy-MM-dd'),
          times: selectedTimes,
          communication_method: communicationMethod,
          patient_id: selectedPatient,
          scheduled_jobs: scheduledJobs
        });
        onClose();
      }
    } catch (error) {
      console.error('Failed to create schedule:', error);
      toast.error('Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    // Disable past dates
    return date < new Date().setHours(0, 0, 0, 0);
  };

  const isDateBooked = (date) => {
    // Check if date is already booked
    return existingSchedules.some(schedule => 
      isSameDay(parseISO(schedule.date), date)
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Schedule Appointment</h2>
            <p className="text-sm text-gray-600">Create a new appointment or meeting</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Calendar and Time */}
            <div className="space-y-6">
              {/* Calendar */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Select Date</h3>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setCurrentMonth(addDays(currentMonth, -30))}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      ←
                    </button>
                    <span className="text-sm font-medium">
                      {format(currentMonth, 'MMMM yyyy')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentMonth(addDays(currentMonth, 30))}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      →
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, index) => {
                    const isDisabled = isDateDisabled(day);
                    const isBooked = isDateBooked(day);
                    const isSelected = isSameDay(day, selectedDate);
                    const isCurrentDay = isToday(day);
                    
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => !isDisabled && setSelectedDate(day)}
                        disabled={isDisabled}
                        className={`
                          p-2 text-sm rounded-lg transition-colors
                          ${isDisabled 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : isBooked
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : isSelected
                            ? 'bg-blue-600 text-white'
                            : isCurrentDay
                            ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                            : 'hover:bg-gray-100'
                          }
                        `}
                      >
                        {format(day, 'd')}
                        {isBooked && <div className="w-1 h-1 bg-red-500 rounded-full mx-auto mt-1"></div>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time Selection */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Select Time(s)</h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>Indian Standard Time (IST)</span>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {timeSlots.map(time => {
                    const isDisabled = isTimeDisabled(time);
                    const isSelected = selectedTimes.includes(time);
                    
                    return (
                      <button
                        key={time}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                            setSelectedTimes(prev => 
                              prev.includes(time)
                                ? prev.filter(t => t !== time)
                                : [...prev, time]
                            );
                          }
                        }}
                        className={`
                          p-2 text-sm rounded-lg border transition-colors
                          ${isDisabled
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : isSelected
                              ? 'border-blue-600 bg-blue-50 text-blue-600 hover:bg-blue-100'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                        title={isDisabled ? 'This time has already passed' : ''}
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {selectedTimes.length > 0
                    ? `Selected: ${selectedTimes.join(', ')} (IST)`
                    : 'No time slots selected'}
                </div>
                {(() => {
                  const disabledCount = timeSlots.filter(time => isTimeDisabled(time)).length;
                  const totalCount = timeSlots.length;
                  const availableCount = totalCount - disabledCount;
                  
                  if (disabledCount > 0) {
                    return (
                      <div className="mt-1 text-xs text-gray-500">
                        {availableCount} of {totalCount} time slots available
                        {disabledCount > 0 && ` (${disabledCount} already passed)`}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Right Column - Details and Communication */}
            <div className="space-y-6">
              {/* Basic Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    {...register('title', { required: 'Title is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Appointment title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Appointment description or notes"
                  />
                </div>  

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Patient
                  </label>
                  <select
                    value={selectedPatient}
                    onChange={(e) => setSelectedPatient(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a patient</option>
                    {patients.map(patient => (
                      <option key={patient.id} value={patient.id}>
                        {patient.alias}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Communication Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Communication Method
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {communicationMethods.map(method => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setCommunicationMethod(method.id)}
                        className={`
                          p-4 border-2 rounded-lg transition-colors flex items-center space-x-3
                          ${communicationMethod === method.id
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }
                        `}
                      >
                        <Icon className={`h-5 w-5 ${method.color}`} />
                        <span className="text-sm font-medium">{method.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  {...register('notes')}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Any additional notes or instructions..."
                />
              </div>

              {/* Summary */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Schedule Summary</h4>
                <div className="space-y-1 text-sm text-blue-800">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span>{selectedTimes.length > 0 ? selectedTimes.join(', ') + ' (IST)' : 'No time selected'}</span>
                  </div>
                  {selectedPatient && (
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>{patients.find(p => p.id == selectedPatient)?.alias}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    {React.createElement(communicationMethods.find(m => m.id === communicationMethod)?.icon, { className: "h-4 w-4" })}
                    <span>{communicationMethods.find(m => m.id === communicationMethod)?.label}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || selectedTimes.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Create Schedule</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SchedulingModal; 